import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const LandingPageSettings = () => {
  const [settings, setSettings] = useState({
    headline: '',
    subheadline: '',
    input_placeholder: '',
    cta_text: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      const { data, error } = await supabase.from('landing_page_content').select('key, value');
      if (error) {
        console.error('Error fetching landing page settings:', error);
        toast({ title: 'Error', description: 'Could not fetch settings.', variant: 'destructive' });
      } else {
        const newSettings = data.reduce((acc, item) => {
          if (item.key in acc) {
            acc[item.key] = item.value;
          }
          return acc;
        }, { ...settings });
        setSettings(newSettings);
      }
      setLoading(false);
    };
    fetchSettings();
  }, [toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettings(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const updates = Object.entries(settings).map(([key, value]) => ({
      key,
      value,
    }));

    const { error } = await supabase.from('landing_page_content').upsert(updates, { onConflict: 'key' });

    if (error) {
      console.error('Error saving landing page settings:', error);
      toast({ title: 'Error', description: 'Could not save settings.', variant: 'destructive' });
    } else {
      toast({ title: 'Success', description: 'Landing page content updated.' });
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Landing Page Content</CardTitle>
        <CardDescription>
          Control the text displayed on the public landing page.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="headline">Headline</Label>
          <Input
            id="headline"
            name="headline"
            value={settings.headline}
            onChange={handleInputChange}
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="subheadline">Sub-headline</Label>
          <Input
            id="subheadline"
            name="subheadline"
            value={settings.subheadline}
            onChange={handleInputChange}
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="input_placeholder">Chat Input Placeholder</Label>
          <Input
            id="input_placeholder"
            name="input_placeholder"
            value={settings.input_placeholder}
            onChange={handleInputChange}
            disabled={saving}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="cta_text">Call to Action Text</Label>
          <Input
            id="cta_text"
            name="cta_text"
            value={settings.cta_text}
            onChange={handleInputChange}
            disabled={saving}
          />
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save Changes
        </Button>
      </CardContent>
    </Card>
  );
};

export default LandingPageSettings;